'use client';

// Runtime imports - functions, classes, values
import {
  Firestore,
  Timestamp,
  QueryConstraint,
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  addDoc,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  writeBatch,
  increment,
} from 'firebase/firestore';

// Type-only imports - interfaces, types
import type {
  DocumentData,
  WithFieldValue,
} from 'firebase/firestore';

import { getStorage, ref, getDownloadURL, uploadBytes } from 'firebase/storage';
import { db as rawDb } from './firebase';

// ==== UTILITY: Always get a defined Firestore instance or throw ====
function getDb(): Firestore {
  if (!rawDb) throw new Error('Firestore client is not initialized!');
  return rawDb;
}

// ==== UNIFIED TIMESTAMP HANDLING ====
export function handleTimestamp(timestamp: any): Date {
  // Handle null/undefined
  if (!timestamp) return new Date();
  
  // Already a Date object
  if (timestamp instanceof Date) return timestamp;
  
  // Firestore Timestamp with toDate method
  if (timestamp && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  
  // Serialized timestamp with seconds/nanoseconds
  if (timestamp && typeof timestamp === 'object' && 'seconds' in timestamp) {
    return new Timestamp(
      timestamp.seconds, 
      timestamp.nanoseconds || 0
    ).toDate();
  }
  
  // Handle _seconds/_nanoseconds format (from Cloud Functions)
  if (timestamp && typeof timestamp === 'object' && '_seconds' in timestamp) {
    return new Timestamp(
      timestamp._seconds, 
      timestamp._nanoseconds || 0
    ).toDate();
  }
  
  // Unix timestamp in seconds
  if (typeof timestamp === 'number') {
    // Handle both seconds and milliseconds
    const date = timestamp > 1000000000000 ? new Date(timestamp) : new Date(timestamp * 1000);
    return date;
  }
  
  // Fallback: try to parse as date string
  try {
    const parsedDate = new Date(timestamp);
    if (isValidDate(parsedDate)) {
      return parsedDate;
    }
    return new Date();
  } catch {
    console.warn('Unknown timestamp format:', timestamp);
    return new Date();
  }
}

export function formatTimestamp(timestamp: any, format: 'full' | 'relative' | 'short' | 'date' | 'time' = 'full'): string {
  const date = handleTimestamp(timestamp);
  
  switch (format) {
    case 'relative':
      return getRelativeTime(date);
    case 'short':
      return date.toLocaleDateString();
    case 'date':
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric', 
        year: 'numeric' 
      });
    case 'time':
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true
      });
    case 'full':
    default:
      return date.toLocaleString();
  }
}

// Helper for relative time (like "2 hours ago")
function getRelativeTime(date: Date): string {
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} minutes ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} hours ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} days ago`; // 30 days
  if (diffInSeconds < 31536000) return `${Math.floor(diffInSeconds / 2592000)} months ago`; // 12 months
  return `${Math.floor(diffInSeconds / 31536000)} years ago`;
}

// Enhanced validation helper
function isValidDate(date: Date): boolean {
  return date instanceof Date && !isNaN(date.getTime());
}

// Enhanced date formatting with better error handling
export function formatDate(date: Date | string | null | undefined, options?: Intl.DateTimeFormatOptions): string {
  if (!date) return 'N/A';
  
  try {
    const dateObj = typeof date === 'string' ? handleTimestamp(date) : date;
    if (!isValidDate(dateObj)) return 'Invalid Date';
    
    return new Intl.DateTimeFormat('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      ...options,
    }).format(dateObj);
  } catch (error) {
    console.warn('Error formatting date:', error);
    return 'Invalid Date';
  }
}

export function formatTime(date: Date | string | null | undefined): string {
  if (!date) return 'N/A';
  
  try {
    const dateObj = typeof date === 'string' ? handleTimestamp(date) : date;
    if (!isValidDate(dateObj)) return 'Invalid Time';
    
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(dateObj);
  } catch (error) {
    console.warn('Error formatting time:', error);
    return 'Invalid Time';
  }
}

export function formatDateTime(date: Date | string | null | undefined): string {
  if (!date) return 'N/A';
  
  try {
    const dateObj = typeof date === 'string' ? handleTimestamp(date) : date;
    if (!isValidDate(dateObj)) return 'Invalid DateTime';
    
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(dateObj);
  } catch (error) {
    console.warn('Error formatting datetime:', error);
    return 'Invalid DateTime';
  }
}

// ==== TYPES ====
export type WithId<T = DocumentData> = T & { id: string };

export interface InterpreterProfile {
  userId?: string;
  name: string;
  firstName: string;
  lastName?: string;
  bio: string;
  location: string;
  timeZone: string;
  languages: string[];
  industries: string[];
  keywords: string;
  culturalKnowledge: string;
  education: string;
  hourlyRate: number;
  availability: boolean;
  available: boolean;
  isVerified: boolean;
  rating: number;
  reviewCount?: number;
  totalReviews: number;
  completedSessions: number;
  profileImage?: string;
  profilePicture?: string;
  createdAt?: string | Timestamp | Date;
  updatedAt?: string | Timestamp | Date;
  lastActiveAt?: string | Timestamp | Date;
  joinedAt?: string | Timestamp | Date;
  contactInfo?: { email?: string; phone?: string };
  experience?: number;
  certifications?: string[];
  specializations?: string[];
  responseRate?: number;
  searchTags?: string[];
  profileCompleteness?: number;
  verificationStatus?: 'pending' | 'verified' | 'rejected';
  profileViews?: number;
  [key: string]: any;
}

// Enhanced search filters interface with more options
export interface InterpreterSearchFilters {
  languages?: string[];
  industries?: string[];
  minRating?: number;
  maxRate?: number;
  minRate?: number;
  isVerified?: boolean;
  location?: string;
  searchTags?: string[];
  searchQuery?: string;
  sortBy?: 'rating' | 'rate' | 'reviews' | 'recent';
  sortOrder?: 'asc' | 'desc';
  limit?: number;
  offset?: number;
  startAfterDoc?: any;
}

// ==== ENHANCED PROFILE UPDATE ====
export async function updateInterpreterProfile(
  userId: string,
  publicUpdates: Partial<InterpreterProfile>,
  privateUpdates?: Record<string, any>,
  profileImageFile?: File
): Promise<boolean> {
  try {
    const db = getDb();
    let profileImageUrl: string | undefined;

    // Upload profile image if provided. Path must match storage.rules:
    // profile-pictures/{uid}/{fileName}, with a jpg/jpeg/png/webp extension.
    if (profileImageFile) {
      const storage = getStorage();
      const extByType: Record<string, string> = {
        'image/jpeg': 'jpg', 'image/jpg': 'jpg', 'image/png': 'png', 'image/webp': 'webp',
      };
      const ext = extByType[profileImageFile.type] || 'jpg';
      const imageRef = ref(storage, `profile-pictures/${userId}/profile_${Date.now()}.${ext}`);
      
      try {
        await uploadBytes(imageRef, profileImageFile, { contentType: profileImageFile.type });
        profileImageUrl = await getDownloadURL(imageRef);
        console.log('✅ Profile image uploaded successfully');
      } catch (uploadError) {
        console.error('❌ Profile image upload failed:', uploadError);
        // Stop before saving anything, so the caller can show the error
        throw Object.assign(
          new Error('Your profile picture could not be uploaded. Please use a JPG, PNG or WebP image between 1 KB and 5 MB.'),
          { code: 'profile-picture-upload-failed' }
        );
      }
    }

    // Enhanced profile completeness calculation
    const calculateCompleteness = (data: Partial<InterpreterProfile>): number => {
      const requiredFields = [
        { field: data.firstName, weight: 10 },
        { field: data.bio && data.bio.length > 50, weight: 15 },
        { field: data.location, weight: 10 },
        { field: data.timeZone, weight: 5 },
        { field: data.languages?.length && data.languages.length > 0, weight: 15 },
        { field: data.industries?.length && data.industries.length > 0, weight: 15 },
        { field: data.keywords && data.keywords.length > 10, weight: 10 },
        { field: data.education && data.education.length > 20, weight: 10 },
        { field: data.culturalKnowledge && data.culturalKnowledge.length > 10, weight: 5 },
        { field: data.hourlyRate && data.hourlyRate > 0, weight: 5 },
        { field: profileImageUrl || data.profileImage, weight: 10 },
      ];
      
      const totalWeight = requiredFields.reduce((sum, item) => sum + item.weight, 0);
      const completedWeight = requiredFields
        .filter(item => Boolean(item.field))
        .reduce((sum, item) => sum + item.weight, 0);
      
      return Math.round((completedWeight / totalWeight) * 100);
    };

    // Prepare and clean public profile updates
    const publicUpdateData: Record<string, any> = {
      ...publicUpdates,
      updatedAt: new Date(),
      lastActiveAt: new Date(),
    };
    
    // Only add profile image fields if we have a URL
    if (profileImageUrl) {
      publicUpdateData.profileImage = profileImageUrl;
      publicUpdateData.profilePicture = profileImageUrl;
    }

    // Add profile completeness
    publicUpdateData.profileCompleteness = calculateCompleteness({ 
      ...publicUpdates, 
      ...(profileImageUrl && { profileImage: profileImageUrl })
    });

    // Enhanced search tags generation
    if (publicUpdates.languages || publicUpdates.industries || publicUpdates.keywords || publicUpdates.location) {
      const searchTags = new Set<string>();
      
      // Add languages with normalization
      if (publicUpdates.languages) {
        publicUpdates.languages.forEach(lang => {
          if (typeof lang === 'string' && lang.trim()) {
            searchTags.add(lang.toLowerCase().trim());
            if (lang.includes('(')) {
              const parts = lang.split('(');
              if (parts.length > 0 && parts[0]) {
                const cleanLang = parts[0].trim().toLowerCase();
                searchTags.add(cleanLang);
              }
            }
          }
        });
      }
      
      // Add industries with normalization
      if (publicUpdates.industries) {
        publicUpdates.industries.forEach(industry => {
          if (typeof industry === 'string' && industry.trim()) {
            searchTags.add(industry.toLowerCase().trim());
            // Add industry synonyms for better search
            const words = industry.toLowerCase().split(/[\s&]+/);
            words.forEach(word => {
              if (word.length > 3) searchTags.add(word);
            });
          }
        });
      }
      
      // Add keywords with better parsing
      if (publicUpdates.keywords) {
        publicUpdates.keywords.split(/[,\n]+/).forEach(keyword => {
          const trimmed = keyword.trim().toLowerCase();
          if (trimmed && trimmed.length > 2) {
            searchTags.add(trimmed);
          }
        });
      }
      
      // Add location components
      if (publicUpdates.location) {
        const locationParts = publicUpdates.location.toLowerCase().split(/[,\s]+/);
        locationParts.forEach(part => {
          if (part.trim() && part.trim().length > 1) {
            searchTags.add(part.trim());
          }
        });
      }
      
      publicUpdateData.searchTags = Array.from(searchTags);
    }

    // Use batch for atomic updates
    const batch = writeBatch(db);
    
    // Update public profile
    const publicRef = doc(db, 'interpreters', userId);
    batch.update(publicRef, publicUpdateData);

    // Update private profile if provided
    if (privateUpdates && Object.keys(privateUpdates).length) {
      const privateUpdateData: Record<string, any> = {
        ...privateUpdates,
        updatedAt: new Date(),
      };
      const privateRef = doc(db, 'interpreters', userId, 'private', 'profile');
      batch.set(privateRef, privateUpdateData, { merge: true });
    }

    await batch.commit();
    return true;
  } catch (error) {
    handleFirestoreError(error, `updateInterpreterProfile(${userId})`);
    throw error;
  }
}

// ==== GENERIC READ HELPERS ====
export async function getDocument<T = DocumentData>(path: string): Promise<WithId<T> | null> {
  try {
    const snap = await getDoc(doc(getDb(), path));
    return snap.exists() ? ({ id: snap.id, ...convertTimestamps(snap.data()) } as WithId<T>) : null;
  } catch (error) {
    handleFirestoreError(error, `getDocument(${path})`);
    throw error;
  }
}

export async function getCollection<T = DocumentData>(
  collectionPath: string,
  constraints: QueryConstraint[] = []
): Promise<WithId<T>[]> {
  try {
    const collRef = collection(getDb(), collectionPath);
    const q = constraints.length ? query(collRef, ...constraints) : collRef;
    const snap = await getDocs(q);
    return snap.docs.map(d => ({ id: d.id, ...convertTimestamps(d.data()) } as WithId<T>));
  } catch (error) {
    handleFirestoreError(error, `getCollection(${collectionPath})`);
    throw error;
  }
}

// ==== STORAGE HELPERS ====
export async function getProfileImageUrl(interpreterId: string, filename?: string): Promise<string> {
  try {
    const storage = getStorage();
    const possibleFiles = filename
      ? [filename]
      : [`${interpreterId}.jpg`, `${interpreterId}.jpeg`, `${interpreterId}.png`, `${interpreterId}.webp`];
    
    for (const file of possibleFiles) {
      try {
        return await getDownloadURL(ref(storage, `profile-pictures/${file}`));
      } catch {
        continue;
      }
    }
    return '/default-avatar.png';
  } catch {
    return '/default-avatar.png';
  }
}

// ==== ENHANCED INTERPRETER READS ====
export async function getInterpreterPublicProfile(interpreterId: string): Promise<WithId<InterpreterProfile> | null> {
  try {
    const profile = await getDocument<InterpreterProfile>(`interpreters/${interpreterId}`);
    if (!profile) return null;

    // Handle profile image with fallback
    const existingImage = profile.profileImage || profile.profilePicture;
    if (!existingImage || (!existingImage.startsWith('http') && !existingImage.startsWith('/'))) {
      profile.profileImage = await getProfileImageUrl(interpreterId, existingImage);
    }
    
    // Ensure required fields have robust defaults
    profile.userId = profile.userId || interpreterId;
    profile.totalReviews = profile.totalReviews ?? profile.reviewCount ?? 0;
    profile.completedSessions = profile.completedSessions ?? 0;
    profile.rating = profile.rating ?? 0;
    profile.available = profile.available ?? profile.availability ?? false;
    profile.isVerified = profile.isVerified ?? false;
    profile.languages = Array.isArray(profile.languages) ? profile.languages : [];
    profile.industries = Array.isArray(profile.industries) ? profile.industries : [];
    profile.keywords = profile.keywords ?? '';
    profile.culturalKnowledge = profile.culturalKnowledge ?? '';
    profile.education = profile.education ?? '';
    profile.bio = profile.bio ?? '';
    profile.firstName = profile.firstName ?? profile.name ?? 'Unknown';
    profile.location = profile.location ?? 'Location not specified';
    profile.timeZone = profile.timeZone ?? 'UTC';
    profile.responseRate = profile.responseRate ?? 0;
    profile.searchTags = Array.isArray(profile.searchTags) ? profile.searchTags : [];
    profile.profileCompleteness = profile.profileCompleteness ?? 0;
    profile.profileViews = profile.profileViews ?? 0;
    
    return profile;
  } catch (error) {
    handleFirestoreError(error, `getInterpreterPublicProfile(${interpreterId})`);
    throw error;
  }
}

export async function getInterpreterIds(): Promise<string[]> {
  try {
    const interpreters = await getCollection('interpreters', [
      where('available', '==', true),
      orderBy('rating', 'desc'),
    ]);
    return interpreters.map(i => i.id);
  } catch (error) {
    handleFirestoreError(error, 'getInterpreterIds()');
    return [];
  }
}

export async function getAllInterpreters(limitCount?: number): Promise<WithId<InterpreterProfile>[]> {
  try {
    const constraints: QueryConstraint[] = [
      where('available', '==', true),
      orderBy('rating', 'desc'),
    ];
    
    if (limitCount && limitCount > 0) {
      constraints.push(limit(limitCount));
    }
    
    return await getCollection<InterpreterProfile>('interpreters', constraints);
  } catch (error) {
    handleFirestoreError(error, 'getAllInterpreters()');
    throw error;
  }
}

export async function getInterpretersByLanguage(language: string, limitCount?: number): Promise<WithId<InterpreterProfile>[]> {
  try {
    const constraints: QueryConstraint[] = [
      where('languages', 'array-contains', language),
      where('available', '==', true),
      orderBy('rating', 'desc'),
    ];
    
    if (limitCount && limitCount > 0) {
      constraints.push(limit(limitCount));
    }
    
    return await getCollection<InterpreterProfile>('interpreters', constraints);
  } catch (error) {
    handleFirestoreError(error, `getInterpretersByLanguage(${language})`);
    throw error;
  }
}

export async function getInterpretersByIndustry(industry: string, limitCount?: number): Promise<WithId<InterpreterProfile>[]> {
  try {
    const constraints: QueryConstraint[] = [
      where('industries', 'array-contains', industry),
      where('available', '==', true),
      orderBy('rating', 'desc'),
    ];
    
    if (limitCount && limitCount > 0) {
      constraints.push(limit(limitCount));
    }
    
    return await getCollection<InterpreterProfile>('interpreters', constraints);
  } catch (error) {
    handleFirestoreError(error, `getInterpretersByIndustry(${industry})`);
    throw error;
  }
}

// Enhanced search function with pagination support
export async function searchInterpreters(filters: InterpreterSearchFilters = {}): Promise<WithId<InterpreterProfile>[]> {
  try {
    const constraints: QueryConstraint[] = [
      where('available', '==', true),
    ];

    // Add language filter
    if (filters.languages && filters.languages.length > 0) {
      constraints.push(where('languages', 'array-contains-any', filters.languages.slice(0, 10))); // Firestore limit
    }

    // Add industry filter
    if (filters.industries && filters.industries.length > 0) {
      constraints.push(where('industries', 'array-contains-any', filters.industries.slice(0, 10))); // Firestore limit
    }

    // Add rating filter
    if (filters.minRating && filters.minRating > 0) {
      constraints.push(where('rating', '>=', filters.minRating));
    }

    // Add rate filters
    if (filters.maxRate && filters.maxRate > 0) {
      constraints.push(where('hourlyRate', '<=', filters.maxRate));
    }
    if (filters.minRate && filters.minRate > 0) {
      constraints.push(where('hourlyRate', '>=', filters.minRate));
    }

    // Add verification filter
    if (filters.isVerified === true) {
      constraints.push(where('isVerified', '==', true));
    }

    // Add sorting
    const sortBy = filters.sortBy || 'rating';
    const sortOrder = filters.sortOrder || 'desc';
    
    switch (sortBy) {
      case 'rate':
        constraints.push(orderBy('hourlyRate', sortOrder));
        break;
      case 'reviews':
        constraints.push(orderBy('totalReviews', sortOrder));
        break;
      case 'recent':
        constraints.push(orderBy('lastActiveAt', sortOrder));
        break;
      default:
        constraints.push(orderBy('rating', sortOrder));
    }
    
    // Add pagination
    if (filters.startAfterDoc) {
      constraints.push(startAfter(filters.startAfterDoc));
    }
    
    // Add limit
    if (filters.limit && filters.limit > 0) {
      constraints.push(limit(Math.min(filters.limit, 50))); // Cap at 50 for performance
    }

    let results = await getCollection<InterpreterProfile>('interpreters', constraints);

    // Client-side filtering for complex queries that Firestore can't handle
    if (filters.searchQuery && filters.searchQuery.trim()) {
      const searchTerm = sanitizeSearchQuery(filters.searchQuery);
      results = results.filter(interpreter => 
        interpreter.firstName?.toLowerCase().includes(searchTerm) ||
        interpreter.lastName?.toLowerCase().includes(searchTerm) ||
        interpreter.bio?.toLowerCase().includes(searchTerm) ||
        interpreter.keywords?.toLowerCase().includes(searchTerm) ||
        interpreter.location?.toLowerCase().includes(searchTerm) ||
        interpreter.languages?.some(lang => lang.toLowerCase().includes(searchTerm)) ||
        interpreter.industries?.some(ind => ind.toLowerCase().includes(searchTerm)) ||
        interpreter.searchTags?.some(tag => tag.includes(searchTerm))
      );
    }

    // Location filtering (client-side for flexibility)
    if (filters.location && filters.location.trim()) {
      const locationTerm = sanitizeSearchQuery(filters.location);
      results = results.filter(interpreter => 
        interpreter.location?.toLowerCase().includes(locationTerm)
      );
    }

    return results;
  } catch (error) {
    handleFirestoreError(error, 'searchInterpreters()');
    throw error;
  }
}

// ==== MUTATION HELPERS ====
export async function addDocument<T extends WithFieldValue<DocumentData>>(collectionPath: string, data: T): Promise<string> {
  try {
    const collRef = collection(getDb(), collectionPath);
    const docRef = await addDoc(collRef, data);
    return docRef.id;
  } catch (error) {
    handleFirestoreError(error, `addDocument(${collectionPath})`);
    throw error;
  }
}

export async function setDocument<T extends WithFieldValue<DocumentData>>(path: string, data: T, options?: { merge?: boolean }): Promise<void> {
  try {
    await setDoc(doc(getDb(), path), data, { merge: options?.merge ?? false });
  } catch (error) {
    handleFirestoreError(error, `setDocument(${path})`);
    throw error;
  }
}

export async function updateDocument<T extends Partial<WithFieldValue<DocumentData>>>(path: string, data: T): Promise<void> {
  try {
    await updateDoc(doc(getDb(), path), data);
  } catch (error) {
    handleFirestoreError(error, `updateDocument(${path})`);
    throw error;
  }
}

export async function deleteDocument(path: string): Promise<void> {
  try {
    await deleteDoc(doc(getDb(), path));
  } catch (error) {
    handleFirestoreError(error, `deleteDocument(${path})`);
    throw error;
  }
}

// ==== ENHANCED TIMESTAMP CONVERSION ====
export function convertTimestamps(obj: any): any {
  if (obj instanceof Timestamp) return obj.toDate();
  
  // Handle serialized Firestore timestamps
  if (obj && typeof obj === 'object' && 'seconds' in obj && typeof obj.seconds === 'number') {
    return new Timestamp(obj.seconds, obj.nanoseconds || 0).toDate();
  }
  
  // Handle _seconds format
  if (obj && typeof obj === 'object' && '_seconds' in obj && typeof obj._seconds === 'number') {
    return new Timestamp(obj._seconds, obj._nanoseconds || 0).toDate();
  }
  
  if (obj && typeof obj === 'object') {
    const copy: any = Array.isArray(obj) ? [] : {};
    for (const key in obj) {
      copy[key] = convertTimestamps(obj[key]);
    }
    return copy;
  }
  return obj;
}

function handleFirestoreError(error: any, op: string): void {
  console.error('[Firestore]', {
    message: error?.message,
    code: error?.code,
    operation: op,
    timestamp: new Date().toISOString(),
    stack: error?.stack?.split('\n').slice(0, 2),
  });
}

// ==== USER HELPERS ====
export async function getUserCollection<T = DocumentData>(uid: string, collectionName: string): Promise<WithId<T>[]> {
  return getCollection<T>(`users/${uid}/${collectionName}`);
}

// ==== TALKSIE-SPECIFIC UTILITIES ====
export function formatLanguagePair(source: string | null | undefined, target: string | null | undefined): string {
  if (!source || !target) return 'N/A';
  return `${source} → ${target}`;
}

export function calculateSessionDuration(start: Date | string | null | undefined, end: Date | string | null | undefined): string {
  if (!start || !end) return 'N/A';
  
  try {
    const startDate = typeof start === 'string' ? handleTimestamp(start) : start;
    const endDate = typeof end === 'string' ? handleTimestamp(end) : end;
    
    if (!isValidDate(startDate) || !isValidDate(endDate)) return 'Invalid';
    
    const diffMs = endDate.getTime() - startDate.getTime();
    if (diffMs < 0) return 'Invalid';
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  } catch (error) {
    console.warn('Error calculating session duration:', error);
    return 'Invalid';
  }
}

export function formatRating(rating: number | null | undefined): string {
  if (rating === null || rating === undefined || isNaN(rating)) return 'New';
  if (rating <= 0) return 'New';
  return rating.toFixed(1);
}

export function formatLanguageList(languages: string[] | null | undefined): string {
  if (!Array.isArray(languages) || languages.length === 0) return 'N/A';
  
  const validLanguages = languages.filter(lang => lang && typeof lang === 'string');
  if (validLanguages.length === 0) return 'N/A';
  
  if (validLanguages.length <= 2) {
    return validLanguages.join(' & ');
  }
  
  return `${validLanguages.slice(0, 2).join(', ')} +${validLanguages.length - 2} more`;
}

export function getAvailabilityStatus(available: boolean | null | undefined, lastActive?: Date | string | null): {
  status: 'available' | 'busy' | 'away' | 'offline';
  label: string;
  color: string;
} {
  if (!available) {
    return {
      status: 'offline',
      label: 'Unavailable',
      color: 'text-gray-500'
    };
  }
  
  if (!lastActive) {
    return {
      status: 'available',
      label: 'Available',
      color: 'text-green-600'
    };
  }
  
  try {
    const lastActiveDate = handleTimestamp(lastActive);
    const timeSinceActive = Date.now() - lastActiveDate.getTime();
    const minutesSinceActive = timeSinceActive / (1000 * 60);
    
    if (minutesSinceActive > 60) {
      return {
        status: 'away',
        label: 'Away',
        color: 'text-yellow-600'
      };
    }
    
    return {
      status: 'available',
      label: 'Available',
      color: 'text-green-600'
    };
  } catch (error) {
    console.warn('Error calculating availability status:', error);
    return {
      status: 'available',
      label: 'Available',
      color: 'text-green-600'
    };
  }
}

// ==== ENHANCED UTILITIES FOR PHASE 2+ ====
export function calculateMatchScore(
  userRequirements: {
    languages: string[];
    industries?: string[];
    maxRate?: number;
    location?: string;
  },
  interpreterProfile: InterpreterProfile
): number {
  let score = 0;
  let maxPossibleScore = 100;
  
  // Language match (40% weight)
  const languageMatches = userRequirements.languages.filter(lang => 
    interpreterProfile.languages?.includes(lang)
  ).length;
  const languageScore = (languageMatches / Math.max(userRequirements.languages.length, 1)) * 40;
  score += languageScore;
  
  // Industry match (25% weight)
  if (userRequirements.industries?.length && interpreterProfile.industries?.length) {
    const industryMatches = userRequirements.industries.filter(industry => 
      interpreterProfile.industries?.includes(industry)
    ).length;
    score += (industryMatches / userRequirements.industries.length) * 25;
  } else if (!userRequirements.industries?.length) {
    score += 12.5; // Partial credit if no industry preference
  }
  
  // Rate compatibility (15% weight)
  if (userRequirements.maxRate && interpreterProfile.hourlyRate) {
    if (interpreterProfile.hourlyRate <= userRequirements.maxRate) {
      score += 15;
    } else {
      // Penalty for being over budget
      const overBudgetRatio = interpreterProfile.hourlyRate / userRequirements.maxRate;
      score += Math.max(0, 15 - (overBudgetRatio - 1) * 20);
    }
  } else if (!userRequirements.maxRate) {
    score += 7.5; // Partial credit if no rate preference
  }
  
  // Location proximity (10% weight) - simplified for now
  if (userRequirements.location && interpreterProfile.location) {
    const locationMatch = interpreterProfile.location.toLowerCase()
      .includes(userRequirements.location.toLowerCase());
    if (locationMatch) score += 10;
  }
  
  // Rating bonus (5% weight)
  if (interpreterProfile.rating && interpreterProfile.rating > 0) {
    score += (interpreterProfile.rating / 5) * 5;
  }
  
  // Verification bonus (5% weight)
  if (interpreterProfile.isVerified) {
    score += 5;
  }
  
  return Math.round(Math.min(score, maxPossibleScore));
}

export function sanitizeSearchQuery(query: string | null | undefined): string {
  if (!query || typeof query !== 'string') return '';
  
  return query
    .trim()
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters except hyphens
    .replace(/\s+/g, ' ') // Normalize whitespace
    .slice(0, 100); // Limit length
}

// ==== ANALYTICS & PERFORMANCE HELPERS ====
export async function incrementProfileViews(interpreterId: string): Promise<void> {
  try {
    const profileRef = doc(getDb(), 'interpreters', interpreterId);
    await updateDoc(profileRef, {
      profileViews: increment(1),
      lastViewedAt: new Date()
    });
  } catch (error) {
    // Log but don't throw - view counting is not critical
    console.warn('Failed to increment profile views:', error);
  }
}

// Enhanced batch operations for better performance
export async function getMultipleInterpreters(ids: string[]): Promise<(WithId<InterpreterProfile> | null)[]> {
  try {
    if (ids.length === 0) return [];
    
    // ✅ FIXED: Add explicit check to ensure ids[0] exists
    if (ids.length === 1 && ids[0]) {  // Added && ids[0] check
      const profile = await getInterpreterPublicProfile(ids[0]);
      return [profile];
    }
    
    // Split into batches of 10 (Firestore limit for 'in' queries)
    const batches = [];
    for (let i = 0; i < ids.length; i += 10) {
      batches.push(ids.slice(i, i + 10));
    }
    
    const results: (WithId<InterpreterProfile> | null)[] = new Array(ids.length).fill(null);
    
    for (const batch of batches) {
      try {
        const batchResults = await getCollection<InterpreterProfile>('interpreters', [
          where('__name__', 'in', batch.map(id => doc(getDb(), 'interpreters', id)))
        ]);
        
        // Map results back to original positions
        batch.forEach((id, batchIndex) => {
          const originalIndex = ids.indexOf(id);
          const found = batchResults.find(result => result.id === id);
          if (found && originalIndex !== -1) {
            results[originalIndex] = found;
          }
        });
      } catch (batchError) {
        console.warn(`Batch query failed for IDs: ${batch.join(', ')}`, batchError);
        // Try individual requests for this batch as fallback
        for (const id of batch) {
          try {
            const profile = await getInterpreterPublicProfile(id);
            const originalIndex = ids.indexOf(id);
            if (originalIndex !== -1) {
              results[originalIndex] = profile;
            }
          } catch (individualError) {
            console.warn(`Failed to fetch individual profile: ${id}`, individualError);
          }
        }
      }
    }
    
    return results;
  } catch (error) {
    handleFirestoreError(error, 'getMultipleInterpreters()');
    throw error;
  }
}

// ==== BULK OPERATIONS ====
export async function bulkUpdateProfiles(
  updates: Array<{ id: string; data: Partial<InterpreterProfile> }>
): Promise<{ success: number; failed: number; errors: any[] }> {
  const db = getDb();
  const batchSize = 500; // Firestore batch limit
  let processed = 0;
  let failed = 0;
  const errors: any[] = [];

  try {
    for (let i = 0; i < updates.length; i += batchSize) {
      const batch = writeBatch(db);
      const chunk = updates.slice(i, i + batchSize);

      chunk.forEach(({ id, data }) => {
        try {
          const docRef = doc(db, 'interpreters', id);
          batch.update(docRef, {
            ...data,
            updatedAt: new Date()
          });
        } catch (error) {
          failed++;
          errors.push({ id, error });
        }
      });

      try {
        await batch.commit();
        processed += chunk.length - (chunk.length - chunk.filter(u => !errors.some(e => e.id === u.id)).length);
      } catch (batchError) {
        failed += chunk.length;
        errors.push({ batch: chunk.map(u => u.id), error: batchError });
      }
    }

    return { success: processed, failed, errors };
  } catch (error) {
    handleFirestoreError(error, 'bulkUpdateProfiles()');
    throw error;
  }
}

// ==== CACHE HELPERS ====
const profileCache = new Map<string, { data: WithId<InterpreterProfile>; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export async function getCachedInterpreterProfile(interpreterId: string, forceRefresh = false): Promise<WithId<InterpreterProfile> | null> {
  const now = Date.now();
  const cached = profileCache.get(interpreterId);

  if (!forceRefresh && cached && (now - cached.timestamp) < CACHE_DURATION) {
    return cached.data;
  }

  try {
    const profile = await getInterpreterPublicProfile(interpreterId);
    if (profile) {
      profileCache.set(interpreterId, { data: profile, timestamp: now });
    }
    return profile;
  } catch (error) {
    // Return cached data if available, even if expired, on error
    if (cached) {
      console.warn('Using expired cached profile due to fetch error:', error);
      return cached.data;
    }
    throw error;
  }
}

export function clearProfileCache(interpreterId?: string): void {
  if (interpreterId) {
    profileCache.delete(interpreterId);
  } else {
    profileCache.clear();
  }
}

// ==== VALIDATION HELPERS ====
export function validateInterpreterProfile(profile: Partial<InterpreterProfile>): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!profile.firstName || profile.firstName.trim().length < 2) {
    errors.push('First name must be at least 2 characters long');
  }

  if (!profile.bio || profile.bio.trim().length < 50) {
    errors.push('Bio must be at least 50 characters long');
  }

  if (!profile.location || profile.location.trim().length < 3) {
    errors.push('Location must be specified');
  }

  if (!profile.languages || !Array.isArray(profile.languages) || profile.languages.length === 0) {
    errors.push('At least one language must be specified');
  }

  if (!profile.industries || !Array.isArray(profile.industries) || profile.industries.length === 0) {
    errors.push('At least one industry must be specified');
  }

  if (profile.hourlyRate !== undefined && (profile.hourlyRate <= 0 || profile.hourlyRate > 1000)) {
    errors.push('Hourly rate must be between $1 and $1000');
  }

  return { valid: errors.length === 0, errors };
}
